import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/Input/Input';
import type { VenueMeetingsBreakdown } from './types';
import { CURRENCY_INFO, type SupportedCurrency } from './types';

export interface MeetingsBreakdownProps {
  shortlistId: string;
  breakdown: VenueMeetingsBreakdown;
  attendees: number;
  displayCurrency: string;
  onChange: (data: Partial<VenueMeetingsBreakdown>) => void;
}

export function MeetingsBreakdown({
  shortlistId,
  breakdown,
  attendees,
  displayCurrency,
  onChange,
}: MeetingsBreakdownProps) {
  const currency = displayCurrency as SupportedCurrency;
  const currencyInfo = CURRENCY_INFO[currency] ?? CURRENCY_INFO.USD;

  // Helper to format currency value from cents to dollars with 2 decimal places
  const formatCurrencyValue = (cents: number | null): string => {
    if (cents === null) return '';
    return (cents / 100).toFixed(2);
  };

  // Track which field is currently being edited
  const [editingField, setEditingField] = useState<string | null>(null);

  // Local state for controlled inputs
  const [localValues, setLocalValues] = useState({
    meeting_room_rental: formatCurrencyValue(breakdown.meeting_room_rental),
    meeting_setup_fees: formatCurrencyValue(breakdown.meeting_setup_fees),
    meeting_av_cost: formatCurrencyValue(breakdown.meeting_av_cost),
    number_of_meetings: breakdown.number_of_meetings?.toString() ?? '0',
    coffee_cost_per_attendee: formatCurrencyValue(breakdown.coffee_cost_per_attendee),
    coffee_count: breakdown.coffee_count?.toString() ?? '0',
  });

  // Sync local values when breakdown prop changes (but not for fields currently being edited)
  useEffect(() => {
    setLocalValues(prev => ({
      meeting_room_rental: editingField === 'meeting_room_rental' ? prev.meeting_room_rental : formatCurrencyValue(breakdown.meeting_room_rental),
      meeting_setup_fees: editingField === 'meeting_setup_fees' ? prev.meeting_setup_fees : formatCurrencyValue(breakdown.meeting_setup_fees),
      meeting_av_cost: editingField === 'meeting_av_cost' ? prev.meeting_av_cost : formatCurrencyValue(breakdown.meeting_av_cost),
      number_of_meetings: editingField === 'number_of_meetings' ? prev.number_of_meetings : (breakdown.number_of_meetings?.toString() ?? '0'),
      coffee_cost_per_attendee: editingField === 'coffee_cost_per_attendee' ? prev.coffee_cost_per_attendee : formatCurrencyValue(breakdown.coffee_cost_per_attendee),
      coffee_count: editingField === 'coffee_count' ? prev.coffee_count : (breakdown.coffee_count?.toString() ?? '0'),
    }));
  }, [breakdown, editingField]);

  const handleChange = (field: keyof typeof localValues, value: string) => {
    // Preserve user input as-is while typing
    setLocalValues(prev => ({ ...prev, [field]: value }));

    // Convert to DB format and notify parent
    const numValue = parseFloat(value) || 0;
    let dbValue: number | null = null;

    if (field === 'coffee_cost_per_attendee' || field === 'meeting_room_rental' || field === 'meeting_setup_fees' || field === 'meeting_av_cost') {
      // Convert dollars to cents
      dbValue = value ? Math.round(numValue * 100) : null;
    } else {
      // Count fields
      dbValue = Math.round(numValue);
    }

    onChange({ [field]: dbValue });
  };

  // Handle focus to track editing state
  const handleFocus = (field: keyof typeof localValues) => {
    setEditingField(field);
  };

  // Format currency values on blur to ensure 2 decimal places
  const handleBlur = (field: keyof typeof localValues) => {
    setEditingField(null);
    const currencyFields = ['coffee_cost_per_attendee', 'meeting_room_rental', 'meeting_setup_fees', 'meeting_av_cost'];
    if (currencyFields.includes(field)) {
      const numValue = parseFloat(localValues[field]);
      if (!isNaN(numValue)) {
        setLocalValues(prev => ({ ...prev, [field]: numValue.toFixed(2) }));
      }
    }
  };

  // Calculate totals for display
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const roomRental = parseFloat(localValues.meeting_room_rental) || 0;
  const setupFees = parseFloat(localValues.meeting_setup_fees) || 0;
  const avCost = parseFloat(localValues.meeting_av_cost) || 0;
  const numberOfMeetings = parseInt(localValues.number_of_meetings) || 0;
  
  // Per-meeting costs multiplied by number of meetings
  const perMeetingTotal = (roomRental + setupFees + avCost) * numberOfMeetings;

  const coffeeCost = parseFloat(localValues.coffee_cost_per_attendee) || 0;
  const coffeeCount = parseInt(localValues.coffee_count) || 0;
  const coffeeTotal = coffeeCost * coffeeCount * attendees;

  const grandTotal = perMeetingTotal + coffeeTotal;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-amber-900">Meeting Costs Breakdown</h3>
        <p className="text-sm text-amber-700 mt-1">Room rental, setup fees, and AV are per meeting</p>
      </div>

      {/* Number of Meetings and Coffee Inputs */}
      <div className="mb-4 space-y-3">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700 w-40">Number of Meetings:</label>
          <div className="w-24">
            <Input
              type="number"
              min="0"
              value={localValues.number_of_meetings}
              onChange={(e) => handleChange('number_of_meetings', e.target.value)}
              className="text-right"
            />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700 w-40">Coffee Cost (per coffee) ({currencyInfo.symbol}):</label>
          <div className="w-36">
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="0.00"
              value={localValues.coffee_cost_per_attendee}
              onChange={(e) => handleChange('coffee_cost_per_attendee', e.target.value)}
              onFocus={() => handleFocus('coffee_cost_per_attendee')}
              onBlur={() => handleBlur('coffee_cost_per_attendee')}
              className="text-right"
              leftIcon={<span className="text-sm text-gray-600 font-medium">{currencyInfo.symbol}</span>}
            />
          </div>
          <label className="text-sm font-medium text-gray-700 w-40">Number of Coffees per Attendee:</label>
          <div className="w-24">
            <Input
              type="number"
              min="0"
              value={localValues.coffee_count}
              onChange={(e) => handleChange('coffee_count', e.target.value)}
              className="text-right"
            />
          </div>
        </div>
      </div>

      <table className="w-full text-sm table-fixed">
        <colgroup>
          <col className="w-auto" />
          <col className="w-40" />
          <col className="w-28" />
          <col className="w-32" />
        </colgroup>
        <thead>
          <tr className="text-left border-b border-amber-200">
            <th className="py-2 px-3 font-medium text-amber-900">Item</th>
            <th className="py-2 px-3 font-medium text-amber-900 text-right">Cost/Meeting ({currencyInfo.symbol})</th>
            <th className="py-2 px-3 font-medium text-amber-900 text-right">Number of Meetings</th>
            <th className="py-2 px-3 font-medium text-amber-900 text-right w-32">Total ({currencyInfo.symbol})</th>
          </tr>
        </thead>
        <tbody>
          {/* Room Rental */}
          <tr className="border-b border-amber-100">
            <td className="py-3 px-3 font-medium text-gray-900">Room Rental</td>
            <td className="py-3 px-3 text-right">
              <div className="flex justify-end">
                <div className="w-36">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={localValues.meeting_room_rental}
                    onChange={(e) => handleChange('meeting_room_rental', e.target.value)}
                    onFocus={() => handleFocus('meeting_room_rental')}
                    onBlur={() => handleBlur('meeting_room_rental')}
                    className="text-right"
                    leftIcon={<span className="text-sm text-gray-600 font-medium">{currencyInfo.symbol}</span>}
                  />
                </div>
              </div>
            </td>
            <td className="py-3 px-3 text-right">
              <div className="flex justify-end">
                <div className="w-24">
                  <Input
                    type="number"
                    min="0"
                    value={localValues.number_of_meetings}
                    onChange={(e) => handleChange('number_of_meetings', e.target.value)}
                    className="text-right"
                  />
                </div>
              </div>
            </td>
            <td className="py-3 px-3 text-right font-medium text-gray-900 w-32">
              <span className="inline-block min-w-[120px] text-right">{formatCurrency(roomRental * numberOfMeetings)}</span>
            </td>
          </tr>

          {/* Setup Fees */}
          <tr className="border-b border-amber-100">
            <td className="py-3 px-3 font-medium text-gray-900">Setup Fees (per meeting)</td>
            <td className="py-3 px-3 text-right">
              <div className="flex justify-end">
                <div className="w-36">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={localValues.meeting_setup_fees}
                    onChange={(e) => handleChange('meeting_setup_fees', e.target.value)}
                    onFocus={() => handleFocus('meeting_setup_fees')}
                    onBlur={() => handleBlur('meeting_setup_fees')}
                    className="text-right"
                    leftIcon={<span className="text-sm text-gray-600 font-medium">{currencyInfo.symbol}</span>}
                  />
                </div>
              </div>
            </td>
            <td className="py-3 px-3 text-right">
              <div className="flex justify-end">
                <div className="w-24">
                  <Input
                    type="number"
                    min="0"
                    value={localValues.number_of_meetings}
                    onChange={(e) => handleChange('number_of_meetings', e.target.value)}
                    className="text-right"
                  />
                </div>
              </div>
            </td>
            <td className="py-3 px-3 text-right font-medium text-gray-900 w-32">
              <span className="inline-block min-w-[120px] text-right">{formatCurrency(setupFees * numberOfMeetings)}</span>
            </td>
          </tr>

          {/* AV */}
          <tr className="border-b border-amber-100">
            <td className="py-3 px-3 font-medium text-gray-900">AV (per meeting)</td>
            <td className="py-3 px-3 text-right">
              <div className="flex justify-end">
                <div className="w-36">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={localValues.meeting_av_cost}
                    onChange={(e) => handleChange('meeting_av_cost', e.target.value)}
                    onFocus={() => handleFocus('meeting_av_cost')}
                    onBlur={() => handleBlur('meeting_av_cost')}
                    className="text-right"
                    leftIcon={<span className="text-sm text-gray-600 font-medium">{currencyInfo.symbol}</span>}
                  />
                </div>
              </div>
            </td>
            <td className="py-3 px-3 text-right">
              <div className="flex justify-end">
                <div className="w-24">
                  <Input
                    type="number"
                    min="0"
                    value={localValues.number_of_meetings}
                    onChange={(e) => handleChange('number_of_meetings', e.target.value)}
                    className="text-right"
                  />
                </div>
              </div>
            </td>
            <td className="py-3 px-3 text-right font-medium text-gray-900 w-32">
              <span className="inline-block min-w-[120px] text-right">{formatCurrency(avCost * numberOfMeetings)}</span>
            </td>
          </tr>

          {/* Total Row */}
          <tr className="bg-amber-100">
            <td className="py-3 px-3 font-bold text-amber-900" colSpan={3}>
              Total Meeting Costs (including coffee)
            </td>
            <td className="py-3 px-3 text-right font-bold text-amber-900 w-32">
              <span className="inline-block min-w-[120px] text-right">{formatCurrency(grandTotal)}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

